"use client"

import { useState } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { HttpTypes } from "@medusajs/types"
import { useT } from "@lib/i18n/use-t"

type TreeItem = HttpTypes.StoreProductCategory & {
  kids: HttpTypes.StoreProductCategory[]
}

export default function CategorySidebar({
  tree,
  activeCategoryId,
  countryCode: _countryCode,
}: {
  tree: TreeItem[]
  activeCategoryId?: string
  countryCode: string
}) {
  const t = useT()
  /* Expand parents that contain the active category */
  const initialExpanded = new Set<string>()
  tree.forEach((parent) => {
    if (
      parent.id === activeCategoryId ||
      parent.kids.some((k) => k.id === activeCategoryId)
    ) {
      initialExpanded.add(parent.id)
    }
  })
  /* Also expand first 2 by default */
  tree.slice(0, 2).forEach((p) => initialExpanded.add(p.id))

  const [expanded, setExpanded] = useState(initialExpanded)

  const toggle = (id: string) => {
    const next = new Set(expanded)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setExpanded(next)
  }

  return (
    <div className="sticky top-[72px] rounded-2xl border border-slate-200/60 bg-white p-3 dark:border-white/8 dark:bg-[#111827]">
      <div className="mb-2.5 px-1.5 text-[10px] font-bold uppercase tracking-widest text-brand-light dark:text-brand-dark">
        {t("categories")}
      </div>

      {/* "Усі товари" */}
      <LocalizedClientLink
        href="/store"
        className={`mb-0.5 flex items-center justify-between rounded-lg px-2 py-1.5 text-xs font-semibold transition
          ${
            !activeCategoryId
              ? "bg-brand-light/8 text-brand-light dark:bg-brand-dark/10 dark:text-brand-dark"
              : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5"
          }`}
      >
        {t("allProducts")}
      </LocalizedClientLink>

      {tree.map((parent) => {
        const isParentActive = parent.id === activeCategoryId
        const hasActiveChild = parent.kids.some(
          (k) => k.id === activeCategoryId
        )
        const isExpanded = expanded.has(parent.id)

        return (
          <div key={parent.id}>
            {/* Parent row */}
            <div
              className={`flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 transition
                ${
                  isParentActive || hasActiveChild
                    ? "bg-brand-light/8 dark:bg-brand-dark/10"
                    : "hover:bg-slate-50 dark:hover:bg-white/5"
                }`}
              onClick={() => toggle(parent.id)}
            >
              <span onClick={(e) => e.stopPropagation()}>
                <LocalizedClientLink
                  href={`/categories/${parent.handle}`}
                  className={`text-xs font-semibold transition
                    ${
                      isParentActive
                        ? "text-brand-light dark:text-brand-dark"
                        : "text-slate-700 dark:text-slate-200"
                    }`}
                >
                  {parent.name}
                </LocalizedClientLink>
              </span>
              <div className="flex items-center gap-1.5">
                {parent.kids.length > 0 && (
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className={`text-slate-400 transition-transform ${
                      isExpanded ? "rotate-90" : ""
                    }`}
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                )}
              </div>
            </div>

            {/* Children */}
            {isExpanded &&
              parent.kids.map((child) => {
                const isChildActive = child.id === activeCategoryId
                return (
                  <LocalizedClientLink
                    key={child.id}
                    href={`/categories/${child.handle}`}
                    className={`flex items-center justify-between rounded-md py-1 pl-6 pr-2 text-[12px] transition
                      ${
                        isChildActive
                          ? "bg-brand-light/8 font-semibold text-brand-light dark:bg-brand-dark/10 dark:text-brand-dark"
                          : "text-slate-500 hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200"
                      }`}
                  >
                    {child.name}
                  </LocalizedClientLink>
                )
              })}
          </div>
        )
      })}
    </div>
  )
}
